import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useEquipmentScope } from '../equipmentScope'
import { FileText, Info, Link2, Paperclip, Plus, Search, X } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useEquipment } from '@/hooks/useEquipment'
import { formsByEventType } from '@/lib/taskForms'
import { procedureTypeForPurpose } from '@/lib/equipment'
import { useTaskFormTemplates } from '@/hooks/useTaskFormTemplates'
import { useEventTypes } from '@/hooks/useEventTypes'
import { useAssignableUsers } from '@/hooks/useAssignableUsers'
import {
  REPEAT_CYCLES,
  REPEAT_CYCLE_LABELS,
  type CreateEventPayload,
  type RepeatCycle,
} from '@/api/equipmentApi'
import {
  isSchedulableEventType,
  type MaintenanceTask,
  type MaintenanceTaskClass,
} from '@/types/maintenanceTask'

/** The mock-mode display fallback (a worklist row) prepended optimistically. */
export type NewEventInput = Omit<MaintenanceTask, 'id' | 'status'>

const UNASSIGNED = 'none'
// Radix Select cannot hold an empty-string value, so "does not repeat" needs a
// sentinel of its own rather than ''.
const NO_REPEAT = 'no-repeat'
// The Form select's "whatever the cycle links" choice, for the same reason.
const CYCLE_FORM = 'cycle-form'
const PRIORITIES = [
  { v: '1', label: '1 — Urgent' },
  { v: '2', label: '2 — High' },
  { v: '3', label: '3 — Normal' },
  { v: '4', label: '4 — Low' },
  { v: '5', label: '5 — Deferred' },
]

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className='space-y-1'>
      <label className='text-xs font-medium text-gray-600'>
        {label}
        {hint && <span className='ml-1 font-normal text-gray-400'>· {hint}</span>}
      </label>
      {children}
    </div>
  )
}

export function NewEventModal({
  open,
  onClose,
  onCreate,
}: {
  open: boolean
  onClose: () => void
  onCreate: (payload: CreateEventPayload, fallback: NewEventInput) => void
}) {
  // Only this page's units: a room cannot get an equipment event, nor the reverse.
  const { purposes, copy } = useEquipmentScope()
  const { data: units } = useEquipment(purposes)
  const { eventTypes } = useEventTypes()
  const { users } = useAssignableUsers()

  const [taskClass, setTaskClass] = useState<MaintenanceTaskClass>('corrective')
  const [equipmentId, setEquipmentId] = useState('') // unit id
  const [eventType, setEventType] = useState('')
  const [assigneeId, setAssigneeId] = useState(UNASSIGNED) // user id, or UNASSIGNED
  const [priority, setPriority] = useState('2')
  const [earlyStart, setEarlyStart] = useState(todayIso())
  const [dueDate, setDueDate] = useState(todayIso())
  // '' = does not repeat. Preventive only: recurring CORRECTIVE maintenance is
  // a contradiction — corrective means something broke — and the server rejects
  // it, so the control is not offered for a CM.
  const [repeatCycle, setRepeatCycle] = useState<RepeatCycle | ''>('')
  // A form picked for this task (z8nrz7fjzc), or CYCLE_FORM for the cycle's.
  const [formId, setFormId] = useState(CYCLE_FORM)
  const [planHours, setPlanHours] = useState('1.5')
  const [reason, setReason] = useState('')
  const [sop, setSop] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Every tenant event type — including ones the user added in Manage Event Types —
  // regardless of the CM/PM class toggle (class is a separate attribute, not
  // derivable from the code), so custom event types are always selectable here.
  // …minus the display-only codes. `process` is in the tenant catalog so
  // reservations resolve a label and colour, but the server rejects it as an
  // event type — offering it here would only produce a 400.
  const eventOptions = eventTypes.filter((t) => isSchedulableEventType(t.code))

  useEffect(() => {
    if (!open) return
    setTaskClass('corrective')
    setEquipmentId('')
    setEventType('')
    setAssigneeId(UNASSIGNED)
    setPriority('2')
    setEarlyStart(todayIso())
    setDueDate(todayIso())
    setPlanHours('1.5')
    setReason('')
    setSop('')
    setRepeatCycle('')
    setFormId(CYCLE_FORM)
    setAttachments([])
  }, [open])

  // Another unit may call for another kind of form.
  useEffect(() => setFormId(CYCLE_FORM), [equipmentId])

  const addFiles = (files: FileList | null) => {
    if (!files?.length) return
    setAttachments((prev) => [...prev, ...Array.from(files)])
  }
  const removeFile = (idx: number) => setAttachments((prev) => prev.filter((_, i) => i !== idx))

  // Default the event type to the first option of the current class.
  useEffect(() => {
    if (eventOptions.length && !eventOptions.some((t) => t.code === eventType)) {
      setEventType(eventOptions[0].code)
    }
  }, [eventOptions, eventType])

  const pickClass = (next: MaintenanceTaskClass) => {
    setTaskClass(next)
    // Switching to Corrective drops any cadence chosen while it was Preventive;
    // leaving it set would send a value the server rejects on a control the
    // user can no longer see.
    if (next === 'corrective') setRepeatCycle('')
  }

  const selected = useMemo(() => units?.find((u) => u.id === equipmentId), [units, equipmentId])
  // The form each event type carries on the unit's type (z8nrz7fgzw): a task
  // of that event type starts straight into it, and completing it ends the task.
  const forms = useMemo(() => formsByEventType(selected?.schedule, repeatCycle || null), [selected, repeatCycle])
  const cycleForm = forms.get(eventType)
  // Manual work on a type with no cycle still needs a form (z8nrz7fjzc): any
  // form of the unit's kind can be picked for the task, over the cycle's.
  const { data: templates = [] } = useTaskFormTemplates(selected ? procedureTypeForPurpose(selected.purpose) : null)
  const picked = formId === CYCLE_FORM ? undefined : templates.find((t) => t.id === formId)
  const form = picked ?? cycleForm
  const isCM = taskClass === 'corrective'
  const canSubmit = equipmentId !== '' && eventType !== '' && (!isCM || reason.trim() !== '')

  const submit = () => {
    if (!canSubmit || !selected) return
    const priorityNum = Number(priority) || 3
    const hoursNum = Number(planHours) || undefined
    const assignee = assigneeId === UNASSIGNED ? undefined : assigneeId
    const assigneeName = users.find((u) => u.id === assigneeId)?.name ?? 'Unassigned'
    onCreate(
      {
        equipmentId: selected.id,
        eventTypeCode: eventType,
        taskClass,
        dueDate,
        earlyStart: earlyStart || undefined,
        priority: priorityNum,
        planHours: hoursNum,
        sopRef: sop.trim() || undefined,
        reason: reason.trim() || undefined,
        assigneeId: assignee,
        repeatCycle: repeatCycle || undefined,
        procedureTemplateId: picked?.id,
      },
      {
        taskClass,
        equipmentId: selected.equipmentId,
        usedBy: '',
        eventType,
        priority: priorityNum,
        earlyStart,
        dueDate,
        planHours: hoursNum ?? 1,
        assigned: assigneeName,
      },
    )
    onClose()
  }

  const classColor = isCM ? '#d97706' : '#2563eb'

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[92vh] gap-0 overflow-y-auto p-0 sm:max-w-3xl'>
        {/* Header */}
        <div className='flex items-baseline gap-3 border-b border-gray-100 bg-gray-50/60 px-6 py-4'>
          <h2 className='text-lg font-semibold tracking-tight text-gray-900'>New Event</h2>
          <p className='text-xs text-muted-foreground'>
            {copy.newEventHint}
          </p>
        </div>

        <div className='space-y-4 px-6 py-5'>
          {/* Event class */}
          <div>
            <p className='mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
              Event class
            </p>
            <div className='flex flex-wrap items-center gap-3'>
              <div className='inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5'>
                {(['corrective', 'preventive'] as const).map((c) => {
                  const active = taskClass === c
                  const color = c === 'corrective' ? '#d97706' : '#2563eb'
                  return (
                    <button
                      key={c}
                      type='button'
                      onClick={() => pickClass(c)}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                        active ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-800',
                      )}
                      style={active ? { color } : undefined}
                    >
                      <span
                        className='h-2 w-2 rounded-full'
                        style={{ backgroundColor: active ? color : '#9ca3af' }}
                      />
                      {c === 'corrective' ? 'Corrective (CM)' : 'Preventive (PM)'}
                    </button>
                  )
                })}
              </div>
              <span
                className='inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium'
                style={{ backgroundColor: `${classColor}14`, color: classColor }}
              >
                <span className='h-1.5 w-1.5 rounded-full' style={{ backgroundColor: classColor }} />
                {isCM ? 'Unplanned / on-demand' : 'Planned / recurring'}
              </span>
            </div>
          </div>

          {/* Equipment + auto-fill info bar */}
          <Field label={copy.unit}>
            <div className='flex flex-wrap items-stretch gap-2'>
              <div className='relative min-w-[260px] flex-1'>
                <Search className='pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Select value={equipmentId} onValueChange={setEquipmentId}>
                  <SelectTrigger className='w-full pl-9'>
                    <SelectValue placeholder={`Search ${copy.unitsLower}…`} />
                  </SelectTrigger>
                  <SelectContent>
                    {(units ?? []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.equipmentId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='flex min-w-[280px] flex-[2] items-center gap-2 rounded-md border border-blue-100 bg-blue-50/50 px-3 py-2 text-xs text-gray-600'>
                <Info className='h-4 w-4 shrink-0 text-blue-500' />
                {selected ? (
                  <span className='truncate'>
                    {selected.group} · {selected.location.display} · SN {selected.serial}
                  </span>
                ) : (
                  <span className='text-gray-400'>Group, location and serial auto-fill here</span>
                )}
              </div>
            </div>
          </Field>

          {/* Type / priority / assignee */}
          <div className='grid gap-3 md:grid-cols-3'>
            <Field label='Event Type'>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger className='w-full'>
                  {/* Just the event type: its form is named in the list and in the hint below. */}
                  <SelectValue placeholder='Select…'>
                    <span className='block truncate'>{eventOptions.find((t) => t.code === eventType)?.label}</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {eventOptions.map((t) => (
                    <SelectItem key={t.code} value={t.code}>
                      <span className='inline-flex items-center gap-1.5'>
                        {t.label}
                        {forms.has(t.code) && (
                          <span className='inline-flex items-center gap-1 text-xs font-medium text-blue-700'>
                            <Link2 className='h-3 w-3' />
                            {forms.get(t.code)?.name}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected && (
                <p className={cn('text-[11px]', form ? 'text-blue-700' : 'text-gray-400')} data-testid='new-event-form-hint'>
                  {form
                    ? `Form: ${form.name} — Start opens it, completing it ends the task.`
                    : forms.size > 0
                      ? `No form for this event type. Forms are linked to ${[...forms.keys()]
                          .map((code) => eventOptions.find((t) => t.code === code)?.label ?? code)
                          .join(', ')}.`
                      : templates.length > 0
                        ? 'No form linked on this type. Pick one under Form, or leave it without.'
                        : 'No form linked on this type.'}
                </p>
              )}
            </Field>
            <Field label='Priority (PRT)'>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className='w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.v} value={p.v}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label='Assign To'>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger className='w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* The form this task runs (z8nrz7fjzc): the cycle's by default. */}
          {selected && (
            <Field label='Form' hint='optional'>
              <Select value={formId} onValueChange={setFormId}>
                <SelectTrigger className='w-full md:w-1/2'>
                  <SelectValue>
                    <span className='block truncate'>
                      {picked?.name ?? (cycleForm ? `${cycleForm.name} (linked to this event type)` : 'No form')}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CYCLE_FORM}>
                    {cycleForm ? `${cycleForm.name} (linked to this event type)` : 'No form'}
                  </SelectItem>
                  {templates
                    .filter((t) => t.id !== cycleForm?.id)
                    .map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {/* Dates / hours */}
          <div className='grid gap-3 md:grid-cols-3'>
            <Field label='Early Start Date'>
              <Input type='date' value={earlyStart} onChange={(e) => setEarlyStart(e.target.value)} />
            </Field>
            <Field label='Due Date'>
              <Input type='date' value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
            {!isCM && (
              <Field label='Repeats'>
                <Select
                  value={repeatCycle === '' ? NO_REPEAT : repeatCycle}
                  onValueChange={(v) => setRepeatCycle(v === NO_REPEAT ? '' : (v as RepeatCycle))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Does not repeat' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_REPEAT}>Does not repeat</SelectItem>
                    {REPEAT_CYCLES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {REPEAT_CYCLE_LABELS[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {repeatCycle !== '' && (
                  <p className='mt-1 text-[11px] text-muted-foreground'>
                    One task, shown on the calendar from the early start date to the due date —
                    or until it is completed.
                  </p>
                )}
              </Field>
            )}
            <Field label='Planned Hrs'>
              <Input
                type='number'
                step='0.5'
                min='0'
                value={planHours}
                onChange={(e) => setPlanHours(e.target.value)}
              />
            </Field>
          </div>

          {/* Reason + SOP */}
          <div className='grid gap-3 md:grid-cols-2'>
            <Field label='Reason / Problem Description' hint={isCM ? 'required for CM' : 'optional'}>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder={isCM ? 'What failed, when, and any immediate action taken…' : 'Notes for this event…'}
                className={cn(
                  'w-full resize-none rounded-md border px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary/30',
                  isCM && reason.trim() === '' ? 'border-amber-300' : 'border-gray-200 focus:border-primary/50',
                )}
              />
            </Field>
            <Field label='Link SOP & Attachments'>
              <div className='space-y-2 rounded-md border border-gray-200 p-3'>
                <Input value={sop} onChange={(e) => setSop(e.target.value)} placeholder='e.g. SOP-CM-072' className='h-8' />
                <div className='flex items-center gap-2 text-xs text-gray-500'>
                  <FileText className='h-4 w-4 text-blue-500' />
                  {sop ? `${sop}.pdf` : form ? `Form: ${form.name}` : 'No SOP linked'}
                </div>
                {attachments.length > 0 && (
                  <ul className='space-y-1'>
                    {attachments.map((f, i) => (
                      <li
                        key={`${f.name}-${i}`}
                        className='flex items-center justify-between gap-2 rounded border border-gray-100 bg-gray-50 px-2 py-1 text-xs text-gray-700'
                      >
                        <span className='truncate'>
                          {f.name} <span className='text-gray-400'>({Math.max(1, Math.round(f.size / 1024))} KB)</span>
                        </span>
                        <button type='button' onClick={() => removeFile(i)} title='Remove' className='shrink-0 text-gray-400 hover:text-red-500'>
                          <X className='h-3.5 w-3.5' />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <input
                  ref={fileInputRef}
                  type='file'
                  multiple
                  className='hidden'
                  onChange={(e) => {
                    addFiles(e.target.files)
                    e.target.value = ''
                  }}
                />
                <button
                  type='button'
                  onClick={() => fileInputRef.current?.click()}
                  className='inline-flex items-center gap-1.5 rounded-md border border-blue-200 px-2.5 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50'
                >
                  <Paperclip className='h-3.5 w-3.5' />
                  Add attachment
                </button>
              </div>
            </Field>
          </div>
        </div>

        {/* Footer */}
        <div className='flex flex-wrap items-center gap-3 border-t border-gray-100 bg-gray-50/60 px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            className='rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50'
          >
            Cancel
          </button>
          <p className='text-[11px] text-gray-400'>
            New {isCM ? 'CM' : 'PM'} starts as Not Started · appears on the Tasks worklist
          </p>
          <button
            type='button'
            disabled={!canSubmit}
            onClick={submit}
            className='ml-auto inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none'
          >
            <Plus className='h-4 w-4' />
            Create Event
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
